import * as THREE from 'three';
import { CalibrationStore } from '@/xr/calibrationStore';
import EventEmitter from 'eventemitter3';
import type { Handedness } from '@/xr/handInput';
import {
  CAPTURE_RADIUS,
  GRAZE_FACTOR,
  VIRTUAL_CLUBS,
  captureRadiusFor,
  type ClubSpec,
  type Impact,
} from '@/physics/swingModel';

/**
 * Controller-swing input for the virtual-golf mode: read the club controller's
 * grip pose every frame, work out where the club *head* is, and detect the
 * moment it sweeps through the ball.
 *
 * The hard constraint is the frame rate. WebXR only surfaces one pose per frame,
 * and a driver head at 45 m/s covers ~0.6 m between frames at 72 Hz — it
 * teleports straight past a 43 mm ball. So impact is a swept-segment test
 * (previous head position → current), which also hands us the sub-frame moment
 * of contact for free: slerping the grip orientation to that parameter is how
 * face angle gets measured at impact rather than a frame either side of it.
 *
 * The geometry model is anchored at the BUTT of the grip, not at the controller:
 *
 *   buttLocal   controller → top of the grip (grip space)
 *   shaftLocal  unit direction down the shaft, butt → head
 *   faceLocal   unit square-face normal, ⊥ shaft
 *   headLocal   = buttLocal + shaftLocal × club.length   (derived per club)
 *
 * Two things fall out of that. First, the controller can be mounted ANYWHERE
 * along the attachment — a YEZRO handle puts it toward one end, so the player's
 * hands are offset from the controller along the shaft line, and butt-anchoring
 * lets the drawn grip sit in their hands rather than on the controller. Second,
 * the head distance comes from the selected club's real length, so switching
 * from a wedge to a driver extends the reach exactly like pulling a longer club
 * from the bag.
 *
 * Calibration solves buttLocal/shaftLocal/faceLocal from poses that are known on
 * both sides (the outline flow), so nothing depends on WebXR's grip-space axis
 * convention or on how the mount happens to be seated.
 */

export type AttachmentId = 'none' | 'yezro-y14' | 'custom';

export type AttachmentPreset = {
  id: AttachmentId;
  label: string;
  /**
   * How far behind the controller the top of the grip sits, metres, along the
   * shaft line. 0 = the butt is at the controller. The YEZRO Y14 mounts the
   * controller toward the end of its 20.5" handle with the real grip behind it,
   * so the offset is large; a bare controller held in a fist is nearly zero.
   */
  buttOffset: number;
};

/**
 * Uncalibrated defaults. The *direction* the shaft is assumed to run in is a
 * guess about grip space that calibration replaces; the butt offsets are rough
 * physical estimates. Treat these as "good enough to see a club", not as
 * playable geometry — the outline calibration measures the real thing.
 *
 * Y14: per the product geometry, the controller mounts at the BOTTOM end of
 * the 20.5" (0.52 m) handle — tracking ring down, effectively becoming the
 * club head end — with the entire grip and shaft above it. So the grip top is
 * a full handle-length behind the controller, and the virtual head extends
 * beyond the controller down the same line.
 */
export const ATTACHMENTS: Record<AttachmentId, AttachmentPreset> = {
  'none': { id: 'none', label: 'Bare controller', buttOffset: 0.06 },
  'yezro-y14': { id: 'yezro-y14', label: 'YEZRO Y14', buttOffset: 0.52 },
  'custom': { id: 'custom', label: 'Custom', buttOffset: 0.20 },
};

/** Cycle order for the wrist-menu attachment button. */
export const ATTACHMENT_ORDER: AttachmentId[] = ['yezro-y14', 'none', 'custom'];

/**
 * Grip-space direction the club is assumed to run in before calibration: down
 * and away from the hand, which is roughly how a Quest controller's handle sits
 * relative to its tracking ring. Overridable via `shaftAxis` (`?clubaxis=`).
 */
const DEFAULT_SHAFT_AXIS = new THREE.Vector3(0, -0.35, -0.94).normalize();
/** Ditto for the square-face normal; flipped for a left-handed player. */
const DEFAULT_FACE_AXIS = new THREE.Vector3(1, 0, 0);

/** Below this the controller is being carried or waggled, not swung. m/s.
 *  Low enough that a soft chip stroke (~2 m/s at the head) still registers. */
const MIN_SWING_MPS = 1.6;
/** The head must be travelling within this of the target line to count. */
const MAX_PATH_OFFLINE_COS = 0.5; // 60°
/** One swing, one shot: minimum gap between accepted impacts, seconds. */
const IMPACT_COOLDOWN_S = 0.8;
/** …and the head must get this far from the ball before re-arming, metres.
 *  A chip's whole backswing is ~0.4 m, so this can't be much bigger. */
const REARM_DISTANCE = 0.35;
/** A miss this close to the ball, low and downrange, is reported as a whiff. */
const WHIFF_RADIUS = 0.4;
const WHIFF_MAX_HEIGHT = 0.7;
/** Fraction of forward shaft lean that de-lofts the club at impact. */
const LEAN_DELOFT_GAIN = 0.5;
/** Plausible controller→ball distance for the sole-at-ball snap, metres. */
const MIN_BALL_DIST = 0.2;
const MAX_BALL_DIST = 1.7;
/** Plausible controller→grip-top distance when locking the outline, metres. */
const MAX_BUTT_DIST = 0.7;

/**
 * Ground rule, Golf+ style: the club may not enter the floor — it slides up
 * its own shaft line just enough to keep the head on the turf, and extends
 * back as the hands rise. Applied to the swept impact head too, so the club
 * the player sees skimming the ground is the club that hits the ball.
 */
/** Face centre sits about this far above the sole — the head's resting height. */
const SOLE_CLEARANCE = 0.025;
/** Cap on the slide so a near-horizontal shaft can't telescope absurdly. */
const MAX_GROUND_SLIDE = 0.5;
/** Denominator floor: below this much shaft "downness", stop extending the slide. */
const MIN_SHAFT_DOWN = 0.25;

/** xr-standard gamepad mapping: 4 = A/X, 5 = B/Y. */
const BUTTON_PRIMARY = 4;
const BUTTON_SECONDARY = 5;
/**
 * 3 = thumbstick press. Cycles which pair of axes the stick trims — six degrees
 * of freedom don't fit on one stick, and it's the only input left: the trigger
 * accepts, A/X snaps to the ball, B/Y cancels.
 */
const BUTTON_STICK = 3;
/** xr-standard thumbstick axes, used for the fine-tune stage. */
const AXIS_STICK_X = 2;
const AXIS_STICK_Y = 3;
/**
 * Fine-tune feel: finer base steps than the first iteration (0.5 cm / 0.5°),
 * with HOLD ACCELERATION — a hold past a second sweeps up to 4× per repeat —
 * so a tap is a precision nudge and a hold still crosses 30 cm quickly. Every
 * step fires a short haptic detent so adjustments are felt, not just seen.
 */
const STICK_THRESHOLD = 0.6;
const TUNE_SHIFT_STEP = 0.005;
const TUNE_TWIST_STEP = 0.5;
const MAX_TUNE_SHIFT = 0.5;
const MAX_TUNE_TWIST = 30;
/**
 * The off-axis trims. Tighter limits than slide and twist on purpose: those two
 * correct where the controller sits along the handle, which can be a long way
 * out, whereas these correct how it sits ACROSS it — a mount is at most a few
 * centimetres and a few degrees off, and a wide range here just makes the stick
 * coarse where it most needs to be fine.
 */
const TUNE_OFFSET_STEP = 0.004;
const TUNE_TILT_STEP = 0.4;
const MAX_TUNE_OFFSET = 0.2;
const MAX_TUNE_TILT = 25;
const STICK_REPEAT_DELAY_S = 0.3;
const STICK_REPEAT_S = 0.08;
/** Hold-acceleration ramp: step scale grows 1× → 4× between these hold times. */
const STICK_ACCEL_START_S = 1.0;
const STICK_ACCEL_FULL_S = 2.5;
const STICK_ACCEL_MAX = 4;

/**
 * Calibration-lock robustness. One trigger pull calibrates, but a single grip
 * pose is a single sample of jittery tracking — and a pull mid-reach would
 * calibrate a club nowhere near the outline. So the lock solves from a short
 * AVERAGED window of poses and refuses while the hand is moving.
 */
/** Grip speed above this at the pull = the player is still settling. m/s. */
const LOCK_MAX_SPEED = 0.3;
/** Any sample further than this from the window's mean = not holding still. */
const LOCK_MAX_DEVIATION = 0.015;
/** Any sample rotated more than this from the newest = not holding still. */
const LOCK_MAX_ANGLE_DEG = 8;
/** Pose-averaging window: this much recent history is solved, not one frame. */
const AVG_WINDOW_S = 0.25;
/** The window must actually contain a settled sample run before a lock. */
const AVG_MIN_SAMPLES = 10;
const AVG_MIN_SPAN_S = 0.15;
/** Ring size: 24 frames ≈ 0.33 s at 72 Hz — comfortably spans the window. */
const GRIP_SAMPLE_CAPACITY = 24;

/**
 * Alignment outline placement: SOLED ON THE FLOOR like a club at address, not
 * floating at chest height. Matching a floating outline with a real weighted
 * attachment is awkward; standing the ghost on the turf lets the player simply
 * take their address over it. The butt parks this far ahead of the player at
 * the height the lie geometry dictates; the head lands on the floor out to the
 * club-hand side by itself.
 */
const ALIGN_BUTT_FORWARD = 0.35;
/** Ball-ish height for the outline's head — resting on the turf, not in it. */
const ALIGN_HEAD_HEIGHT = 0.03;
/**
 * Lie of the outline's shaft: down, tilted out to the player's club-hand side
 * by ~28° from vertical — a realistic address lie.
 *
 * The tilt has to be *sideways*, never toward the target, and that is a
 * correctness constraint rather than an aesthetic one. The square-face reference
 * this calibration stores stands for a level, zero-loft face, and the club's
 * basis needs face ⊥ shaft — so a shaft leaning toward the target would force the
 * stored reference to tilt up with it, and every later shot would read that tilt
 * as extra dynamic loft. Leaning it sideways keeps the reference horizontal,
 * which is also how a real club actually sits at address.
 */
const ALIGN_LIE_TILT = 0.53;

const STORAGE_PREFIX = 'fuse.swing.club';

const UP = new THREE.Vector3(0, 1, 0);

/**
 * 'aligning' — the outline is parked and waiting for the player to match it.
 * 'tuning'   — locked AND SAVED; the thumbstick refines (slide along the
 *              attachment, twist the face) with the live club showing every
 *              nudge. Trigger accepts, B/Y reverts to the locked values —
 *              either way the club stays calibrated. Tuning is only ever
 *              entered through a lock; there is no separate adjust flow.
 */
export type AlignmentState = 'off' | 'aligning' | 'tuning';

/**
 * Which pair of axes the thumbstick trims. The refinement is a full rigid-body
 * adjustment — three positions and three rotations — and one stick is two axes,
 * so they come in pairs cycled with a click of the stick.
 *
 *  'length' — slide along the shaft + face twist about it. Where the controller
 *             sits ALONG the handle, and how the face is rolled: the two that
 *             matter most, so this is where tuning always starts.
 *  'offset' — the two positions perpendicular to the shaft. Where the mount sits
 *             ACROSS the handle.
 *  'tilt'   — the two shaft rotations. Corrects a mount clamped at an angle,
 *             which no amount of sliding can fix.
 */
export type TuneAxis = 'length' | 'offset' | 'tilt';

/** Cycle order for the thumbstick-click axis switch. */
export const TUNE_AXES: readonly TuneAxis[] = ['length', 'offset', 'tilt'];

/** The full set of fine-tune trims, relative to the locked calibration. */
export type ClubTuning = {
  /** Which pair the stick is currently driving. */
  axis: TuneAxis;
  /** Slide along the shaft, metres. */
  shift: number;
  /** Face rotation about the shaft, degrees (+ = open). */
  twist: number;
  /** Butt offset along the face normal, metres. */
  offsetFace: number;
  /** Butt offset heel↔toe, metres. */
  offsetSide: number;
  /** Shaft tilt in the face plane — lie angle, degrees. */
  pitch: number;
  /** Shaft tilt across the face plane, degrees. */
  yaw: number;
};

/** Why a lock attempt was refused (or that it succeeded). */
export type LockResult = 'ok' | 'no-grip' | 'moving' | 'too-far';

export type XRSwingControlsOptions = {
  /** Which controller carries the club — the YEZRO mounts on the right. */
  clubHand?: Handedness;
  /** Ball centre in reference space, or undefined when there's nothing to hit. */
  ballWorld: () => THREE.Vector3 | undefined;
  /** Unit target direction in reference space (the anchored aim line). */
  targetDir: () => THREE.Vector3 | undefined;
  /** Blocks impact detection: not anchored, shot in flight, a panel is open… */
  isBlocked?: () => boolean;
  /**
   * Floor height (reference space) under the hitting area, for the ground
   * rule. Defaults to y = 0 (the local-floor origin plane).
   */
  groundY?: () => number;
  /** Strike-capture radius, metres. */
  captureRadius?: number;
  attachment?: AttachmentId;
  /** Overrides the preset's butt offset, metres (`?attach=0.3`). */
  buttOffset?: number;
  /** Grip-space shaft direction override (`?clubaxis=x,y,z`). */
  shaftAxis?: THREE.Vector3;
  /** Grip-space square-face normal override (`?clubface=x,y,z`). */
  faceAxis?: THREE.Vector3;
  /** Club selected at startup, named as in VIRTUAL_CLUBS. */
  initialClub?: string;
  /**
   * Where calibrations are kept. Defaults to a local-only store, so nothing
   * about this class requires a profile code or a network.
   */
  store?: CalibrationStore;
};

interface EventMap {
  impact: (impact: Impact, club: ClubSpec) => void;
  /** Swung near the ball but missed it. */
  whiff: () => void;
  clubChanged: (index: number, club: ClubSpec) => void;
  attachmentChanged: (attachment: AttachmentPreset, calibrated: boolean) => void;
  /** Geometry changed — re-push butt/head/shaft/face to the overlay. The shaft
   *  travels separately because the face centre hangs toe-ward OFF the shaft
   *  line (faceOffset), so it can no longer be derived from butt→head. */
  geometryChanged: (
    buttLocal: THREE.Vector3,
    headLocal: THREE.Vector3,
    shaftLocal: THREE.Vector3,
    faceLocal: THREE.Vector3,
  ) => void;
  calibrated: (attachment: AttachmentPreset) => void;
  /** A controller button edge this mode owns. */
  button: (id: 'menu' | 'club') => void;
  alignmentChanged: (state: AlignmentState) => void;
  /** Live fine-tune trims while 'tuning', including which axis pair is active. */
  tuningChanged: (tuning: ClubTuning) => void;
}

type StoredGeometry = {
  butt: [number, number, number];
  shaft: [number, number, number];
  face: [number, number, number];
  lean: number;
  /**
   * When this calibration was made, epoch ms. Optional because entries written
   * before syncing existed won't have it — those are treated as infinitely old,
   * so a remote copy wins, which is the right way round: a calibration that
   * predates sync has never been anywhere else.
   */
  updatedAt?: number;
};

/** Signed horizontal angle of `vector` relative to `target`, + = right of it. */
function signedRightAngle(vector: THREE.Vector3, target: THREE.Vector3, scratch: THREE.Vector3): number {
  // right = target × up. Facing +Z with +Y up, that is (-1,0,0) — which really is
  // the player's right hand side, and matches the sign BallPhysics gives HLA.
  scratch.set(-target.z, 0, target.x);
  if (scratch.lengthSq() < 1e-9) return 0;
  scratch.normalize();
  const right = vector.x * scratch.x + vector.z * scratch.z;
  const along = vector.x * target.x + vector.z * target.z;
  if (Math.abs(right) < 1e-9 && Math.abs(along) < 1e-9) return 0;
  return THREE.MathUtils.radToDeg(Math.atan2(right, along));
}

export class XRSwingControls extends EventEmitter<EventMap> {
  clubIndex = 0;
  /** Live grip pose of the club controller, reference space. */
  gripPosition = new THREE.Vector3();
  gripQuaternion = new THREE.Quaternion();
  hasGrip = false;
  /** How far the club is currently slid up its shaft to stay out of the floor. */
  groundSlide = 0;
  /** Log every candidate swing, including rejections (`?swingdebug=1`). */
  debug = false;

  #clubHand: Handedness;
  #ballWorld: () => THREE.Vector3 | undefined;
  #targetDir: () => THREE.Vector3 | undefined;
  #isBlocked: () => boolean;
  #groundY: () => number;
  #captureRadius: number;

  #attachment: AttachmentId;
  #buttOffset: number;
  #shaftAxis: THREE.Vector3;
  #faceAxis: THREE.Vector3;
  /** Controller → top of the grip, grip space. */
  #buttLocal = new THREE.Vector3();
  /** Unit direction down the shaft, butt → head, grip space. */
  #shaftLocal = new THREE.Vector3(0, 0, -1);
  /** Unit square-face normal, ⊥ shaft, grip space. */
  #faceLocal = new THREE.Vector3(1, 0, 0);
  /** Derived impact point: butt + shaft × club.length. */
  #headLocal = new THREE.Vector3();
  #leanBase = 0;
  #calibrated = false;

  #head = new THREE.Vector3();
  #prevHead = new THREE.Vector3();
  #prevPrevHead = new THREE.Vector3();
  #prevQuaternion = new THREE.Quaternion();
  #hasPrevious = false;
  #hasPrevPrev = false;
  #elapsed = 0;
  #lastImpactAt = -Infinity;
  #armed = true;
  #buttonsDown = new Map<string, boolean>();
  #clubSource?: XRInputSource;

  // Align-to-outline calibration
  #alignmentState: AlignmentState = 'off';
  #alignGrip = new THREE.Vector3();
  #alignQuaternion = new THREE.Quaternion();
  #alignLength = 1;
  /** Recent grip poses, collected only while 'aligning' — the lock solves
   *  their average, and their spread is the hold-still gate. */
  #gripSamples: { position: THREE.Vector3; quaternion: THREE.Quaternion; time: number }[] = [];
  #gripSpeedEma = 0;
  /** The locked geometry the fine-tune trims are relative to. */
  #store: CalibrationStore;
  /** Timestamp on the loaded local calibration — the sync comparison point. */
  #localUpdatedAt = 0;
  #tuneBaseButt = new THREE.Vector3();
  #tuneBaseFace = new THREE.Vector3();
  #tuneBaseShaft = new THREE.Vector3();
  /**
   * The trim frame, taken ONCE from the locked geometry: F along the face
   * normal, U along heel-toe, both perpendicular to the base shaft. Fixed for
   * the whole tuning session rather than re-derived from the live shaft — a
   * control whose direction rotates as you hold it is unusable, and tilting the
   * shaft would otherwise swing the axes the stick is pushing along.
   */
  #tuneAxisF = new THREE.Vector3();
  #tuneAxisU = new THREE.Vector3();
  #tuneAxis: TuneAxis = 'length';
  #tuneShift = 0;
  #tuneTwist = 0;
  #tuneOffsetFace = 0;
  #tuneOffsetSide = 0;
  #tunePitch = 0;
  #tuneYaw = 0;
  #stickDirection = { x: 0, y: 0 };
  #stickTimer = { x: 0, y: 0 };
  #stickHeldFor = { x: 0, y: 0 };

  // scratch
  #segment = new THREE.Vector3();
  #direction = new THREE.Vector3();
  #closest = new THREE.Vector3();
  #offset = new THREE.Vector3();
  #impactQuaternion = new THREE.Quaternion();
  #accel = new THREE.Vector3();
  #velocity = new THREE.Vector3();
  #faceWorld = new THREE.Vector3();
  #shaftWorld = new THREE.Vector3();
  #toeAxis = new THREE.Vector3();
  #highAxis = new THREE.Vector3();
  #horizontal = new THREE.Vector3();
  #scratch = new THREE.Vector3();
  #matrix = new THREE.Matrix4();
  #axisX = new THREE.Vector3();
  #axisY = new THREE.Vector3();
  #axisZ = new THREE.Vector3();
  #heelAxis = new THREE.Vector3();
  #basisQuaternion = new THREE.Quaternion();

  constructor(options: XRSwingControlsOptions) {
    super();
    // Local-only by default: the library must work with no profile, no network
    // and no Cloudflare account behind it
    this.#store = options.store ?? new CalibrationStore();
    this.#clubHand = options.clubHand ?? 'right';
    this.#ballWorld = options.ballWorld;
    this.#targetDir = options.targetDir;
    this.#isBlocked = options.isBlocked ?? (() => false);
    this.#groundY = options.groundY ?? (() => 0);
    this.#captureRadius = options.captureRadius ?? CAPTURE_RADIUS;
    this.#shaftAxis = (options.shaftAxis ?? DEFAULT_SHAFT_AXIS).clone().normalize();
    this.#faceAxis = (options.faceAxis ?? DEFAULT_FACE_AXIS).clone().normalize();
    if (!options.faceAxis && this.#clubHand === 'left') this.#faceAxis.negate();

    this.#attachment = options.attachment ?? 'yezro-y14';
    this.#buttOffset = options.buttOffset ?? ATTACHMENTS[this.#attachment].buttOffset;

    if (options.initialClub !== undefined) {
      const index = VIRTUAL_CLUBS.findIndex(club => club.name === options.initialClub);
      if (index >= 0) this.clubIndex = index;
      else console.warn(`XRSwingControls: unknown club '${options.initialClub}', keeping default`);
    }

    this.#loadGeometry();
  }

  get club(): ClubSpec {
    return VIRTUAL_CLUBS[this.clubIndex];
  }

  get attachment(): AttachmentPreset {
    return ATTACHMENTS[this.#attachment];
  }

  get isCalibrated(): boolean {
    return this.#calibrated;
  }

  /** Grip-top offset in grip space — feed to ClubOverlay.setGeometry. */
  get buttLocal(): THREE.Vector3 {
    return this.#buttLocal;
  }

  /** Impact point (face centre) in grip space — feed to ClubOverlay.setGeometry. */
  get headLocal(): THREE.Vector3 {
    return this.#headLocal;
  }

  /** Unit shaft direction, butt → head, grip space. */
  get shaftLocal(): THREE.Vector3 {
    return this.#shaftLocal;
  }

  /** Square-face normal in grip space — feed to ClubOverlay.setGeometry. */
  get faceLocal(): THREE.Vector3 {
    return this.#faceLocal;
  }

  /** 1 for a right-handed player, -1 for a left-hander (gear-effect sign). */
  get handSign(): 1 | -1 {
    return this.#clubHand === 'left' ? -1 : 1;
  }

  get captureRadius(): number {
    return this.#captureRadius;
  }

  /** Clean-capture radius for the selected club (base × its face size). */
  get effectiveCaptureRadius(): number {
    return captureRadiusFor(this.#captureRadius, this.club);
  }

  get alignmentState(): AlignmentState {
    return this.#alignmentState;
  }

  /**
   * Pose of the alignment outline, or undefined when not aligning. The pose's
   * own origin is the BUTT of the outline's grip; its geometry is canonical —
   * head at `(0, 0, -length)`, face along `+X` — which is exactly what
   * ClubOverlay draws for `setGeometry(zero, (0,0,-length), (1,0,0))`.
   */
  get alignmentPose(): { position: THREE.Vector3; quaternion: THREE.Quaternion; length: number } | undefined {
    if (this.#alignmentState !== 'aligning') return undefined;
    return { position: this.#alignGrip, quaternion: this.#alignQuaternion, length: this.#alignLength };
  }

  cycleClub(direction: number) {
    const count = VIRTUAL_CLUBS.length;
    this.clubIndex = (this.clubIndex + direction + count) % count;
    // The head reach follows the club's real length — pulling a driver out of
    // the bag extends the club, with the grip staying put in the hands
    this.#recomputeHead();
    this.emit('clubChanged', this.clubIndex, this.club);
    this.emit('geometryChanged', this.#buttLocal, this.#headLocal, this.#shaftLocal, this.#faceLocal);
  }

  cycleAttachment(direction = 1) {
    const order = ATTACHMENT_ORDER;
    const current = Math.max(0, order.indexOf(this.#attachment));
    this.#attachment = order[(current + direction + order.length) % order.length];
    this.#buttOffset = ATTACHMENTS[this.#attachment].buttOffset;
    this.#loadGeometry();
    this.emit('geometryChanged', this.#buttLocal, this.#headLocal, this.#shaftLocal, this.#faceLocal);
    this.emit('attachmentChanged', this.attachment, this.#calibrated);
  }

  /**
   * Park a club outline in front of the player to line their real club up with.
   *
   * This is the calibration that doesn't need the tee, the ball, or any guess
   * about WebXR's grip-space axes: the outline's world pose is known, so once the
   * player has their physical attachment sitting inside it — hands on the
   * outline's grip, shaft along the outline's shaft — one press solves the butt
   * anchor, the shaft direction AND the square-face direction in grip space. The
   * club comes out inline with the attachment by construction.
   */
  beginAlignment(head: { position: THREE.Vector3; quaternion: THREE.Quaternion }) {
    this.#horizontal.set(0, 0, -1).applyQuaternion(head.quaternion).setY(0);
    if (this.#horizontal.lengthSq() < 1e-6) this.#horizontal.set(0, 0, -1);
    this.#horizontal.normalize();

    this.#alignLength = this.club.length;

    // Face square to the player's gaze, and level — this is the zero-loft
    // reference every later impact is measured against.
    this.#faceWorld.copy(this.#horizontal);
    // Shaft down, tilted out to the club-hand side. Sideways keeps it
    // perpendicular to the level face; see ALIGN_LIE_TILT.
    this.#scratch.crossVectors(this.#horizontal, UP).normalize(); // the player's right
    if (this.#clubHand === 'left') this.#scratch.negate();
    this.#shaftWorld.copy(this.#scratch).multiplyScalar(ALIGN_LIE_TILT).setY(-1).normalize();
    this.#alignQuaternion.copy(this.#basisFor(this.#shaftWorld, this.#faceWorld));

    // Soled at address: butt ahead of the player at whatever height puts the
    // FACE CENTRE exactly on the floor down the lie. The face centre hangs
    // faceOffset toe-ward off the shaft line, and at a real lie the toe axis
    // has a vertical component — account for it or the sole floats/sinks by a
    // couple of centimetres. (Floor is y=0 in local-floor reference space.)
    const toeY = -(
      this.#scratch.crossVectors(this.#heelAxis.copy(this.#shaftWorld).negate(), this.#faceWorld)
        .normalize().y * this.handSign
    );
    this.#alignGrip
      .set(head.position.x, 0, head.position.z)
      .addScaledVector(this.#horizontal, ALIGN_BUTT_FORWARD);
    this.#alignGrip.y = ALIGN_HEAD_HEIGHT
      - this.#shaftWorld.y * this.#alignLength
      - toeY * this.club.faceOffset;

    this.#alignmentState = 'aligning';
    this.#gripSamples.length = 0;
    this.#gripSpeedEma = 0;
    this.emit('alignmentChanged', this.#alignmentState);
  }

  /**
   * Lock the averaged grip pose onto the outline — one pull calibrates and
   * saves. The outline's butt, shaft and face are known in world space, so the
   * whole grip-space geometry is one change of frame:
   *   buttLocal  = gripMatrix⁻¹ · outlineButt
   *   shaftLocal = gripQuat⁻¹  · outlineShaftDir
   *   faceLocal  = gripQuat⁻¹  · outlineFace
   *
   * Robustness: the solve uses the MEAN of the last quarter second of grip
   * poses (position mean, quaternion nlerp — the samples are within a few
   * degrees of each other, nlerp is exact enough), and refuses while the hand
   * is still moving. A single tracked frame is a jitter sample; an average of
   * eighteen isn't.
   */
  lockAlignment(): LockResult {
    if (this.#alignmentState !== 'aligning') return 'no-grip';
    if (!this.hasGrip || this.#gripSamples.length === 0) return 'no-grip';

    // Hold-still gate. Also demands the window is actually FULL of settled
    // samples — a controller picked up a moment ago has a thin buffer, and
    // "hold still" is exactly the corrective instruction for that case too.
    const newest = this.#gripSamples[this.#gripSamples.length - 1];
    const window = this.#gripSamples.filter(sample => newest.time - sample.time <= AVG_WINDOW_S);
    if (
      this.#gripSpeedEma > LOCK_MAX_SPEED
      || window.length < AVG_MIN_SAMPLES
      || newest.time - window[0].time < AVG_MIN_SPAN_S
    ) {
      return 'moving';
    }

    const meanPosition = new THREE.Vector3();
    for (const sample of window) meanPosition.add(sample.position);
    meanPosition.divideScalar(window.length);

    const maxAngleCos = Math.cos(THREE.MathUtils.degToRad(LOCK_MAX_ANGLE_DEG));
    for (const sample of window) {
      if (sample.position.distanceTo(meanPosition) > LOCK_MAX_DEVIATION) return 'moving';
      if (Math.abs(sample.quaternion.dot(newest.quaternion)) < maxAngleCos) return 'moving';
    }

    // Quaternion mean by incremental nlerp, dot-negation guarded (q and -q are
    // the same rotation; averaging across the double-cover flips signs)
    const meanQuaternion = newest.quaternion.clone();
    for (const sample of window) {
      const q = sample.quaternion;
      if (meanQuaternion.dot(q) < 0) {
        meanQuaternion.slerp(this.#impactQuaternion.set(-q.x, -q.y, -q.z, -q.w), 0.5);
      } else {
        meanQuaternion.slerp(q, 0.5);
      }
    }
    meanQuaternion.normalize();

    const buttWorld = this.#alignGrip;
    const shaftWorld = new THREE.Vector3(0, 0, -1).applyQuaternion(this.#alignQuaternion);
    const faceWorld = new THREE.Vector3(1, 0, 0).applyQuaternion(this.#alignQuaternion);

    this.#matrix.compose(meanPosition, meanQuaternion, this.#scratch.set(1, 1, 1)).invert();
    const buttLocal = buttWorld.clone().applyMatrix4(this.#matrix);
    const inverseGrip = this.#impactQuaternion.copy(meanQuaternion).invert();
    const shaftLocal = shaftWorld.clone().applyQuaternion(inverseGrip).normalize();
    const faceLocal = faceWorld.clone().applyQuaternion(inverseGrip).normalize();

    // The controller is mounted ON the attachment, so it can't be far from the
    // grip. A large offset means the trigger was pulled with the controller
    // nowhere near the outline.
    if (buttLocal.length() > MAX_BUTT_DIST) {
      console.warn(`XRSwingControls: alignment rejected, grip ${buttLocal.length().toFixed(2)} m from the controller`);
      return 'too-far';
    }

    this.#buttLocal.copy(buttLocal);
    this.#shaftLocal.copy(shaftLocal);
    this.#faceLocal.copy(faceLocal);
    this.#recomputeHead();
    // Whatever lie the player locked in is their neutral, so impact loft is a
    // change from it — the outline's own face direction stands in for the target.
    this.#leanBase = this.#measureLean(meanQuaternion, faceWorld);
    this.#calibrated = true;
    this.#invalidatePose();
    // Saved NOW — the fine-tune stage that follows refines an already-good
    // calibration, so backing out of it costs nothing.
    this.#storeGeometry();
    this.#enterTuning();
    this.emit('geometryChanged', this.#buttLocal, this.#headLocal, this.#shaftLocal, this.#faceLocal);
    this.emit('alignmentChanged', this.#alignmentState);
    // The one physical "it took" cue
    this.#pulseRaw(1, 60);
    return 'ok';
  }

  /** Arm the fine-tune stage with the current geometry as its base. */
  #enterTuning() {
    this.#rebaseTuning();
    this.#tuneAxis = 'length'; // slide and twist are what most calibrations need
    this.#stickDirection.x = 0;
    this.#stickDirection.y = 0;
    this.#alignmentState = 'tuning';
  }

  /**
   * Adopt the current geometry as the zero point for the trims and rebuild the
   * trim frame from it. Called on entering the stage and again after an A/X ball
   * snap, which replaces the geometry the trims were relative to.
   */
  #rebaseTuning() {
    this.#tuneBaseButt.copy(this.#buttLocal);
    this.#tuneBaseFace.copy(this.#faceLocal);
    this.#tuneBaseShaft.copy(this.#shaftLocal);

    // F: the face normal flattened into the plane perpendicular to the shaft.
    // The fallbacks are for a degenerate lock where face and shaft are parallel
    // — impossible from a real calibration, but a zero-length axis here would
    // put NaN into the geometry and take the drawn club with it.
    this.#tuneAxisF.copy(this.#tuneBaseFace).projectOnPlane(this.#tuneBaseShaft);
    if (this.#tuneAxisF.lengthSq() < 1e-8) {
      this.#tuneAxisF.set(1, 0, 0).projectOnPlane(this.#tuneBaseShaft);
    }
    if (this.#tuneAxisF.lengthSq() < 1e-8) {
      this.#tuneAxisF.set(0, 1, 0).projectOnPlane(this.#tuneBaseShaft);
    }
    this.#tuneAxisF.normalize();
    this.#tuneAxisU.crossVectors(this.#tuneBaseShaft, this.#tuneAxisF).normalize();

    this.#tuneShift = 0;
    this.#tuneTwist = 0;
    this.#tuneOffsetFace = 0;
    this.#tuneOffsetSide = 0;
    this.#tunePitch = 0;
    this.#tuneYaw = 0;
  }

  /** Live fine-tune trims while 'tuning'. */
  get tuning(): ClubTuning {
    return {
      axis: this.#tuneAxis,
      shift: this.#tuneShift,
      twist: this.#tuneTwist,
      offsetFace: this.#tuneOffsetFace,
      offsetSide: this.#tuneOffsetSide,
      pitch: this.#tunePitch,
      yaw: this.#tuneYaw,
    };
  }

  /** Any trim moved off zero — i.e. the stage has unsaved refinement in it. */
  #hasTrims(): boolean {
    return this.#tuneShift !== 0 || this.#tuneTwist !== 0
      || this.#tuneOffsetFace !== 0 || this.#tuneOffsetSide !== 0
      || this.#tunePitch !== 0 || this.#tuneYaw !== 0;
  }

  /** Switch which pair of axes the thumbstick drives (thumbstick click). */
  cycleTuneAxis(direction = 1) {
    if (this.#alignmentState !== 'tuning') return;
    const current = Math.max(0, TUNE_AXES.indexOf(this.#tuneAxis));
    this.#tuneAxis = TUNE_AXES[(current + direction + TUNE_AXES.length) % TUNE_AXES.length];
    // Held-stick state belongs to the axes it was pushing — carrying the repeat
    // timer across would fire a step on the new pair the instant it's selected
    this.#stickDirection.x = 0;
    this.#stickDirection.y = 0;
    this.#pulseRaw(1, 20);
    this.emit('tuningChanged', this.tuning);
  }

  /**
   * Slide the whole club along the attachment line (metres) — thumbstick
   * up/down while 'tuning'. Grip and head move together: this is for "the
   * controller is mounted somewhere else on the handle", which no other
   * control can express.
   */
  nudgeShift(delta: number) {
    if (this.#alignmentState !== 'tuning') return;
    this.#tuneShift = THREE.MathUtils.clamp(this.#tuneShift + delta, -MAX_TUNE_SHIFT, MAX_TUNE_SHIFT);
    this.#applyTuning();
  }

  /** Twist the face open/closed about the shaft (degrees), + = open. */
  nudgeTwist(degrees: number) {
    if (this.#alignmentState !== 'tuning') return;
    this.#tuneTwist = THREE.MathUtils.clamp(this.#tuneTwist + degrees, -MAX_TUNE_TWIST, MAX_TUNE_TWIST);
    this.#applyTuning();
  }

  /**
   * Move the whole club perpendicular to the shaft (metres): `face` along the
   * face normal, `side` along heel-toe. Together with `nudgeShift` this is the
   * full 3-axis position — for a mount that sits off to one side of the handle
   * rather than merely too far up or down it.
   */
  nudgeOffsetFace(delta: number) {
    if (this.#alignmentState !== 'tuning') return;
    this.#tuneOffsetFace = THREE.MathUtils.clamp(this.#tuneOffsetFace + delta, -MAX_TUNE_OFFSET, MAX_TUNE_OFFSET);
    this.#applyTuning();
  }

  nudgeOffsetSide(delta: number) {
    if (this.#alignmentState !== 'tuning') return;
    this.#tuneOffsetSide = THREE.MathUtils.clamp(this.#tuneOffsetSide + delta, -MAX_TUNE_OFFSET, MAX_TUNE_OFFSET);
    this.#applyTuning();
  }

  /**
   * Tilt the shaft (degrees): `pitch` in the face plane — the lie angle — and
   * `yaw` across it. With `nudgeTwist` this completes the three rotations, and
   * these two are the ones no other control could reach: a mount clamped a few
   * degrees off the handle's axis tilts the whole club, and sliding it along a
   * wrong line only moves the error.
   */
  nudgePitch(degrees: number) {
    if (this.#alignmentState !== 'tuning') return;
    this.#tunePitch = THREE.MathUtils.clamp(this.#tunePitch + degrees, -MAX_TUNE_TILT, MAX_TUNE_TILT);
    this.#applyTuning();
  }

  nudgeYaw(degrees: number) {
    if (this.#alignmentState !== 'tuning') return;
    this.#tuneYaw = THREE.MathUtils.clamp(this.#tuneYaw + degrees, -MAX_TUNE_TILT, MAX_TUNE_TILT);
    this.#applyTuning();
  }

  /** Accept the trims: save and finish. */
  acceptTuning(): boolean {
    if (this.#alignmentState !== 'tuning') return false;
    this.#storeGeometry();
    this.#alignmentState = 'off';
    this.#invalidatePose();
    this.emit('alignmentChanged', this.#alignmentState);
    this.emit('calibrated', this.attachment);
    this.#pulseRaw(1, 60);
    return true;
  }

  /**
   * Re-derive the live geometry from the locked base plus all six trims.
   *
   * Order matters and is not arbitrary. The shaft is rotated FIRST, because the
   * slide and the twist are both defined relative to where it ends up — sliding
   * along the old shaft after tilting would move the club off its own line, and
   * twisting about the old one would leave the face out of square. The face
   * takes the same two tilts as the shaft so it stays perpendicular to it, then
   * the twist about the shaft's new direction. Position comes last: the shaft
   * term needs the final direction, and the two offsets are along the fixed trim
   * frame, so they're independent of everything else.
   *
   * Every trim is re-applied from the base each time rather than accumulated
   * onto the live geometry — repeated incremental rotation drifts and can't be
   * undone by winding the stick back the other way.
   */
  #applyTuning() {
    const pitch = THREE.MathUtils.degToRad(this.#tunePitch);
    const yaw = THREE.MathUtils.degToRad(this.#tuneYaw);

    this.#shaftLocal.copy(this.#tuneBaseShaft)
      .applyAxisAngle(this.#tuneAxisU, pitch)
      .applyAxisAngle(this.#tuneAxisF, yaw)
      .normalize();

    this.#faceLocal.copy(this.#tuneBaseFace)
      .applyAxisAngle(this.#tuneAxisU, pitch)
      .applyAxisAngle(this.#tuneAxisF, yaw)
      .applyAxisAngle(this.#shaftLocal, THREE.MathUtils.degToRad(this.#tuneTwist))
      .normalize();

    this.#buttLocal.copy(this.#tuneBaseButt)
      .addScaledVector(this.#shaftLocal, this.#tuneShift)
      .addScaledVector(this.#tuneAxisF, this.#tuneOffsetFace)
      .addScaledVector(this.#tuneAxisU, this.#tuneOffsetSide);

    this.#recomputeHead();
    this.#invalidatePose();
    this.emit('geometryChanged', this.#buttLocal, this.#headLocal, this.#shaftLocal, this.#faceLocal);
    this.emit('tuningChanged', this.tuning);
  }

  /**
   * Thumbstick trim: a tap is one fine step, a hold repeats and ACCELERATES —
   * up to 4× the step once held past a couple of seconds — and every step
   * fires a haptic detent so the adjustment is felt through the attachment.
   */
  #updateTuneStick(dt: number, axes: readonly number[] | undefined) {
    const deflection = (value: number | undefined) =>
      Math.abs(value ?? 0) > STICK_THRESHOLD ? Math.sign(value!) : 0;
    const wanted = {
      x: deflection(axes?.[AXIS_STICK_X]),
      y: deflection(axes?.[AXIS_STICK_Y]),
    };

    for (const axis of ['x', 'y'] as const) {
      const direction = wanted[axis];
      if (direction === 0) {
        this.#stickDirection[axis] = 0;
        this.#stickTimer[axis] = 0;
        this.#stickHeldFor[axis] = 0;
        continue;
      }
      let fire = false;
      if (direction !== this.#stickDirection[axis]) {
        this.#stickDirection[axis] = direction;
        this.#stickTimer[axis] = STICK_REPEAT_DELAY_S;
        this.#stickHeldFor[axis] = 0;
        fire = true;
      } else {
        this.#stickHeldFor[axis] += dt;
        this.#stickTimer[axis] -= dt;
        if (this.#stickTimer[axis] <= 0) {
          this.#stickTimer[axis] = STICK_REPEAT_S;
          fire = true;
        }
      }
      if (!fire) continue;

      const accel = 1 + (STICK_ACCEL_MAX - 1) * THREE.MathUtils.clamp(
        (this.#stickHeldFor[axis] - STICK_ACCEL_START_S) / (STICK_ACCEL_FULL_S - STICK_ACCEL_START_S),
        0,
        1,
      );
      // Stick up (negative Y in the xr-standard mapping) pulls the club up the
      // line toward the hands; stick down slides it out toward the head.
      // Which pair the stick drives is the thumbstick-click selection. Stick up
      // is negative Y in the xr-standard mapping, so the vertical cases negate
      // where "up" should mean "increase".
      if (this.#tuneAxis === 'length') {
        if (axis === 'y') this.nudgeShift(direction * TUNE_SHIFT_STEP * accel);
        else this.nudgeTwist(direction * TUNE_TWIST_STEP * accel);
      } else if (this.#tuneAxis === 'offset') {
        if (axis === 'y') this.nudgeOffsetFace(-direction * TUNE_OFFSET_STEP * accel);
        else this.nudgeOffsetSide(direction * TUNE_OFFSET_STEP * accel);
      } else {
        if (axis === 'y') this.nudgePitch(-direction * TUNE_TILT_STEP * accel);
        else this.nudgeYaw(direction * TUNE_TILT_STEP * accel);
      }
      this.#pulseRaw(1, 8); // detent tick
    }
  }

  /**
   * Abandon the current stage. From 'aligning' nothing has changed yet; from
   * 'tuning' the trims revert to the locked values (which are what was saved),
   * so the club stays exactly as calibrated.
   */
  cancelAlignment() {
    if (this.#alignmentState === 'off') return;
    if (this.#alignmentState === 'tuning' && this.#hasTrims()) {
      this.#tuneShift = 0;
      this.#tuneTwist = 0;
      this.#tuneOffsetFace = 0;
      this.#tuneOffsetSide = 0;
      this.#tunePitch = 0;
      this.#tuneYaw = 0;
      this.#applyTuning();
    }
    this.#alignmentState = 'off';
    this.#gripSamples.length = 0;
    this.#gripSpeedEma = 0;
    this.#invalidatePose();
    this.emit('alignmentChanged', this.#alignmentState);
  }

  /**
   * Snap the head to the ball: sole the club at the ball, aim down the target
   * line, press. The controller sits on the attachment line, so controller→ball
   * IS the shaft direction, and the butt falls one club-length back up it. Kept
   * as the precision refinement (A/X while tuning) — it can't be the first step,
   * because you can't sole a head you can't yet see in the right place.
   */
  calibrate(ballWorld: THREE.Vector3, targetDir: THREE.Vector3): boolean {
    if (!this.hasGrip) return false;

    this.#matrix.compose(this.gripPosition, this.gripQuaternion, this.#scratch.set(1, 1, 1)).invert();
    const ballLocal = ballWorld.clone().applyMatrix4(this.#matrix);
    const distance = ballLocal.length();
    if (distance < MIN_BALL_DIST || distance > MAX_BALL_DIST) {
      console.warn(`XRSwingControls: calibration rejected, head ${distance.toFixed(2)} m from the controller`);
      return false;
    }

    const face = this.#horizontal.copy(targetDir).setY(0);
    if (face.lengthSq() < 1e-6) return false;
    const faceLocal = face.normalize().clone()
      .applyQuaternion(this.#impactQuaternion.copy(this.gripQuaternion).invert());

    // The ball sits at the FACE CENTRE, which hangs faceOffset toe-ward of the
    // shaft line — so the raw controller→ball direction isn't quite the shaft.
    // One fixed-point iteration is plenty: the offset is ~4 cm against ~1 m.
    let shaftLocal = ballLocal.clone().normalize();
    for (let i = 0; i < 2; i++) {
      const heel = new THREE.Vector3()
        .crossVectors(this.#scratch.copy(shaftLocal).negate(), faceLocal)
        .normalize()
        .multiplyScalar(this.handSign);
      // Hosel point = ball pulled back from toe to heel
      shaftLocal = ballLocal.clone().addScaledVector(heel, this.club.faceOffset).normalize();
    }

    // Orthogonal to the shaft, or the club's basis is degenerate
    if (faceLocal.clone().projectOnPlane(shaftLocal).lengthSq() < 1e-4) {
      console.warn('XRSwingControls: calibration rejected, face reference is along the shaft');
      return false;
    }

    this.#shaftLocal.copy(shaftLocal);
    this.#faceLocal.copy(faceLocal.projectOnPlane(shaftLocal).normalize());
    // Butt = hosel-tip minus one club length of shaft
    this.#buttLocal.copy(ballLocal)
      .addScaledVector(
        this.#heelAxis
          .crossVectors(this.#scratch.copy(shaftLocal).negate(), this.#faceLocal)
          .normalize()
          .multiplyScalar(this.handSign),
        this.club.faceOffset,
      )
      .addScaledVector(shaftLocal, -this.club.length);
    this.#recomputeHead();
    // Whatever shaft lean the player addresses with is their neutral, so impact
    // loft is measured as a change from it rather than from perfectly vertical.
    this.#leanBase = this.#measureLean(this.gripQuaternion, this.#horizontal);
    this.#calibrated = true;
    this.#invalidatePose();
    this.#storeGeometry();
    if (this.#alignmentState === 'tuning') {
      // A/X snap mid-tune: the snapped geometry becomes the new base and the
      // stick continues from zero trims. The trim frame is rebuilt with it —
      // the snap can change the shaft, and stale offset axes would then push
      // the club in directions that no longer relate to it.
      this.#rebaseTuning();
      this.emit('tuningChanged', this.tuning);
    }
    this.emit('geometryChanged', this.#buttLocal, this.#headLocal, this.#shaftLocal, this.#faceLocal);
    this.emit('calibrated', this.attachment);
    return true;
  }

  /** The calibration store, for status display and manual sync. */
  get store(): CalibrationStore {
    return this.#store;
  }

  /**
   * Reconcile this attachment's calibration with the remote copy, last write
   * wins in BOTH directions. Resolves true if the local geometry changed.
   *
   * Both directions matter. Pulling only would discard a calibration made
   * offline the moment the network returned; pushing only would never deliver a
   * calibration made on another device, which is the entire reason the remote
   * copy exists. Timestamps decide, and an entry with none — written before
   * syncing existed — is treated as infinitely old, so the remote wins.
   *
   * Safe to call with no profile: the store reports local-only and this is a
   * no-op. Never called automatically mid-session — a calibration changing
   * under a player who is addressing a ball would be worse than a stale one.
   */
  async syncCalibration(): Promise<boolean> {
    if (!this.#store.isRemote) return false;
    const attachment = this.#attachment;
    const remote = await this.#store.fetchRemote(attachment);

    if (!remote) {
      // Nothing up there yet: publish what we have, so the next device finds it
      if (this.#calibrated) {
        const local = this.#store.read(attachment);
        if (local) await this.#store.push(attachment, local);
      }
      return false;
    }

    let remoteAt = 0;
    try {
      remoteAt = Number((JSON.parse(remote) as StoredGeometry).updatedAt ?? 0);
    } catch {
      return false; // unparseable remote — leave the local copy alone
    }
    if (!(remoteAt > this.#localUpdatedAt)) {
      // Ours is newer (or the same): push it up rather than take a step back
      const local = this.#store.read(attachment);
      if (local && this.#calibrated && this.#localUpdatedAt > remoteAt) {
        await this.#store.push(attachment, local);
      }
      return false;
    }

    // Remote wins. Write it into the local layer first so #loadGeometry — which
    // reads locally and validates — is the single path that ever applies a
    // calibration, remote copies included.
    try {
      localStorage.setItem(`${STORAGE_PREFIX}.${attachment}`, remote);
    } catch { /* private mode — the geometry still applies for this session */ }
    this.#loadGeometry();
    this.#invalidatePose();
    this.emit('geometryChanged', this.#buttLocal, this.#headLocal, this.#shaftLocal, this.#faceLocal);
    this.emit('attachmentChanged', this.attachment, this.#calibrated);
    return true;
  }

  /** Forget the calibration for the current attachment and go back to presets. */
  clearCalibration() {
    this.#store.remove(this.#attachment);
    this.#localUpdatedAt = 0;
    this.#calibrated = false;
    this.#applyPreset();
    this.#invalidatePose();
    this.emit('geometryChanged', this.#buttLocal, this.#headLocal, this.#shaftLocal, this.#faceLocal);
    this.emit('attachmentChanged', this.attachment, false);
  }

  /** Mode change / session end: drop pose history so nothing fires on resume. */
  reset() {
    this.cancelAlignment();
    this.hasGrip = false;
    this.groundSlide = 0;
    this.#hasPrevious = false;
    this.#armed = true;
    this.#lastImpactAt = -Infinity;
    this.#buttonsDown.clear();
    this.#clubSource = undefined;
  }

  update(
    dt: number,
    frame: XRFrame,
    referenceSpace: XRReferenceSpace,
    inputSources: readonly (XRInputSource | undefined)[],
  ) {
    this.#elapsed += dt;

    // Button edges come from every controller: a player holding only the club
    // controller still has to be able to open the menu.
    let clubSource: XRInputSource | undefined;
    for (let i = 0; i < inputSources.length; i++) {
      const source = inputSources[i];
      if (!source || (source as any).hand) continue;
      if (source.handedness === this.#clubHand) clubSource = source;
      const gamepad = source.gamepad;
      if (!gamepad) continue;
      this.#checkButton(gamepad, i, BUTTON_PRIMARY, () => this.emit('button', 'club'));
      this.#checkButton(gamepad, i, BUTTON_SECONDARY, () => this.emit('button', 'menu'));
      // Only bound while tuning, so a stick click means nothing during play
      this.#checkButton(gamepad, i, BUTTON_STICK, () => this.cycleTuneAxis(1));
    }
    this.#clubSource = clubSource;

    if (this.#alignmentState === 'tuning') {
      this.#updateTuneStick(dt, clubSource?.gamepad?.axes);
    }

    // gripSpace is nullable — screen/gaze input sources have none
    const gripSpace = clubSource?.gripSpace;
    const pose = gripSpace ? frame.getPose(gripSpace, referenceSpace) : null;
    if (!pose) {
      this.hasGrip = false;
      this.#hasPrevious = false;
      this.#hasPrevPrev = false;
      // Stale poses must not average into a future lock
      this.#gripSamples.length = 0;
      return;
    }

    const p = pose.transform.position;
    const o = pose.transform.orientation;
    this.gripPosition.set(p.x, p.y, p.z);
    this.gripQuaternion.set(o.x, o.y, o.z, o.w);
    this.hasGrip = true;

    // Pose history for the calibration lock — collected only while aligning,
    // so it costs nothing in normal play
    if (this.#alignmentState === 'aligning' && dt > 0) {
      const previous = this.#gripSamples[this.#gripSamples.length - 1];
      if (previous) {
        const instant = previous.position.distanceTo(this.gripPosition) / dt;
        this.#gripSpeedEma = this.#gripSpeedEma * 0.7 + instant * 0.3;
      }
      this.#gripSamples.push({
        position: this.gripPosition.clone(),
        quaternion: this.gripQuaternion.clone(),
        time: this.#elapsed,
      });
      if (this.#gripSamples.length > GRIP_SAMPLE_CAPACITY) this.#gripSamples.shift();
    }

    this.#head.copy(this.#headLocal).applyQuaternion(this.gripQuaternion).add(this.gripPosition);

    // Ground rule: slide the club up its own shaft line so the head never
    // enters the floor. Slide s raises the head by s·(−shaftWorld.y). Applied
    // BEFORE the swing check and before #head is stored as history, so the
    // swept segment, velocity fit and strike location all use the constrained
    // (visible) head — a swing that skims the turf reads as a skim, and low
    // swings connect instead of passing underneath the ball.
    this.#shaftWorld.copy(this.#shaftLocal).applyQuaternion(this.gripQuaternion);
    const deficit = this.#groundY() + SOLE_CLEARANCE - this.#head.y;
    this.groundSlide = deficit > 0
      ? Math.min(deficit / Math.max(-this.#shaftWorld.y, MIN_SHAFT_DOWN), MAX_GROUND_SLIDE)
      : 0;
    if (this.groundSlide > 0) this.#head.addScaledVector(this.#shaftWorld, -this.groundSlide);

    if (this.#hasPrevious && dt > 0) this.#checkSwing(dt);

    this.#prevPrevHead.copy(this.#prevHead);
    this.#hasPrevPrev = this.#hasPrevious;
    this.#prevHead.copy(this.#head);
    this.#prevQuaternion.copy(this.gripQuaternion);
    this.#hasPrevious = true;
  }

  /**
   * Swept-segment strike test for this frame's club-head travel. Everything the
   * shot needs is derived at the closest-approach point, including the sub-frame
   * orientation, so a 13 ms frame doesn't smear the face angle.
   */
  #checkSwing(dt: number) {
    const ball = this.#ballWorld();
    const target = this.#targetDir();
    if (!ball || !target) return;

    this.#segment.subVectors(this.#head, this.#prevHead);
    const segmentLength = this.#segment.length();
    const speed = segmentLength / dt;

    // Re-arm once the club is clear of the ball again, so a follow-through or
    // the next backswing can't be read as a second strike.
    if (!this.#armed
      && this.#elapsed - this.#lastImpactAt > IMPACT_COOLDOWN_S
      && this.#head.distanceTo(ball) > REARM_DISTANCE) {
      this.#armed = true;
    }

    if (speed < MIN_SWING_MPS || segmentLength < 1e-5) return;
    // Lining a club up with an outline, and trimming it afterwards, both involve
    // waving it about right where the ball is
    if (!this.#armed || this.#isBlocked() || this.#alignmentState !== 'off') return;

    this.#direction.copy(this.#segment).divideScalar(segmentLength);
    // Must be travelling downrange — a backswing passing the ball isn't a shot
    this.#horizontal.set(this.#direction.x, 0, this.#direction.z);
    if (this.#horizontal.lengthSq() < 1e-6) return;
    this.#horizontal.normalize();
    const onLine = this.#horizontal.x * target.x + this.#horizontal.z * target.z;
    if (onLine < MAX_PATH_OFFLINE_COS) return;

    // Closest approach of the swept segment to the ball centre
    this.#offset.subVectors(ball, this.#prevHead);
    const t = THREE.MathUtils.clamp(this.#offset.dot(this.#segment) / (segmentLength * segmentLength), 0, 1);
    this.#closest.copy(this.#prevHead).addScaledVector(this.#segment, t);
    this.#offset.subVectors(ball, this.#closest);
    const approach = this.#offset.length();

    // t at the segment's end means the head is STILL APPROACHING — the true
    // pass lives in the next frame's segment, which will see it with an
    // interior t. Firing now would measure the club a frame early: with the
    // graze band widening capture, a downswing can enter the zone one frame
    // before the ball, and the velocity/face at that moment are still several
    // degrees short of what actually arrives at contact.
    if (t >= 1 - 1e-4) return;

    // Clean capture scales with the selected club's face; a graze band beyond
    // it still makes (badly penalised) contact — thin it, don't null it
    const effectiveRadius = captureRadiusFor(this.#captureRadius, this.club);

    if (approach > effectiveRadius * GRAZE_FACTOR) {
      // Only a segment whose closest approach is *interior* has actually gone
      // past the ball. At t = 1 the head merely stopped short this frame — which
      // is what every downswing frame before contact looks like, so treating
      // that as a whiff would disarm the detector one frame before the real
      // strike and swallow nearly every shot.
      const passedTheBall = t > 1e-4 && t < 1 - 1e-4;
      if (passedTheBall && approach < WHIFF_RADIUS && this.#closest.y < WHIFF_MAX_HEIGHT) {
        this.#armed = false;
        this.#lastImpactAt = this.#elapsed;
        if (this.debug) {
          console.log(`swing: whiff, ${(approach * 100).toFixed(1)} cm from the ball at ${speed.toFixed(1)} m/s`);
        }
        this.#pulseRaw(1, 25); // a short full-strength blip — felt, clearly not a hit
        this.emit('whiff');
      }
      return;
    }

    // Sub-frame orientation at contact
    this.#impactQuaternion.copy(this.#prevQuaternion).slerp(this.gripQuaternion, t);
    this.#faceWorld.copy(this.#faceLocal).applyQuaternion(this.#impactQuaternion).normalize();
    this.#shaftWorld.copy(this.#shaftLocal).applyQuaternion(this.#impactQuaternion).normalize();

    // Curvature-corrected velocity AT the contact instant. The chord estimates
    // velocity at the segment's midpoint, but the head is on an arc: through
    // the bottom of a swing the direction sweeps ~15° per frame, so a contact
    // near either end of the segment reads a systematically wrong attack angle
    // and path from the raw chord. Three positions give the local parabola;
    // v(τ) = v_mid + a·dt·(τ−½) evaluates it where the ball actually was.
    let impactSpeed = speed;
    if (this.#hasPrevPrev) {
      this.#accel.copy(this.#head).addScaledVector(this.#prevHead, -2).add(this.#prevPrevHead);
      this.#velocity.copy(this.#segment).divideScalar(dt)
        .addScaledVector(this.#accel, (t - 0.5) / dt);
      const fitSpeed = this.#velocity.length();
      // A tracking glitch makes the parabola explode — trust it only when sane
      if (fitSpeed > speed * 0.5 && fitSpeed < speed * 1.6) {
        impactSpeed = fitSpeed;
        this.#direction.copy(this.#velocity).divideScalar(fitSpeed);
        this.#horizontal.set(this.#direction.x, 0, this.#direction.z);
        if (this.#horizontal.lengthSq() > 1e-6) this.#horizontal.normalize();
      }
    }

    const attackAngle = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(this.#direction.y, -1, 1)));
    const clubPath = signedRightAngle(this.#horizontal, target, this.#scratch);

    this.#horizontal.set(this.#faceWorld.x, 0, this.#faceWorld.z);
    const faceToTarget = this.#horizontal.lengthSq() < 1e-6
      ? 0
      : signedRightAngle(this.#horizontal.normalize(), target, this.#scratch);
    const facePitch = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(this.#faceWorld.y, -1, 1)));
    const lean = this.#measureLean(this.#impactQuaternion, target) - this.#leanBase;
    const dynamicLoft = this.club.loft + facePitch - LEAN_DELOFT_GAIN * lean;

    // Strike location on the face: where the ball sits relative to the head
    // centre, decomposed in the head's own frame at contact. `crown × face`
    // is the axis the overlay builds its head around — and it points at the
    // *heel* for a right-hander, since the player and the hosel are on the same
    // side of the ball. Hence the handedness flip to get a true toe direction.
    this.#highAxis.copy(this.#shaftWorld).negate();
    this.#toeAxis.crossVectors(this.#highAxis, this.#faceWorld)
      .normalize()
      .multiplyScalar(-this.handSign);
    const strikeToe = this.#offset.dot(this.#toeAxis);
    const strikeHigh = this.#offset.dot(this.#highAxis);

    const impact: Impact = {
      clubSpeed: impactSpeed,
      attackAngle,
      clubPath,
      faceToTarget,
      dynamicLoft,
      strikeToe,
      strikeHigh,
      // The in-face-plane miss is what strike quality should care about; the
      // along-normal part just says the swing passed slightly in front or behind.
      missDistance: Math.hypot(strikeToe, strikeHigh),
    };

    this.#armed = false;
    this.#lastImpactAt = this.#elapsed;
    if (this.debug) {
      console.log('swing impact', {
        speed: +impactSpeed.toFixed(2),
        chordSpeed: +speed.toFixed(2),
        attackAngle: +attackAngle.toFixed(1),
        clubPath: +clubPath.toFixed(1),
        faceToTarget: +faceToTarget.toFixed(1),
        dynamicLoft: +dynamicLoft.toFixed(1),
        lean: +lean.toFixed(1),
        strikeToe: +(strikeToe * 100).toFixed(1),
        strikeHigh: +(strikeHigh * 100).toFixed(1),
        approach: +(approach * 100).toFixed(1),
        slide: +(this.groundSlide * 100).toFixed(1),
        t: +t.toFixed(2),
      });
    }
    this.#pulse(impactSpeed);
    this.emit('impact', impact, this.club);
  }

  /**
   * Shaft lean toward the target, degrees, + = hands ahead of the head. Measured
   * from the grip orientation so it works whether or not the club is moving.
   */
  #measureLean(quaternion: THREE.Quaternion, target: THREE.Vector3): number {
    this.#scratch.copy(this.#shaftLocal).applyQuaternion(quaternion).normalize();
    const towardTarget = -(this.#scratch.x * target.x + this.#scratch.z * target.z);
    const down = -this.#scratch.y;
    if (Math.abs(towardTarget) < 1e-6 && Math.abs(down) < 1e-6) return 0;
    return THREE.MathUtils.radToDeg(Math.atan2(towardTarget, down));
  }

  /**
   * Impact haptics — the only physical feedback a virtual ball can give, and
   * the player feels it THROUGH the attachment: the controller hangs at the
   * far end of an aluminium handle that eats most of the amplitude. So
   * intensity is always maximum; only the duration carries the speed feel
   * (a longer buzz reads as a bigger hit even at fixed amplitude).
   */
  #pulse(speed: number) {
    this.#pulseRaw(1, 60 + 40 * THREE.MathUtils.clamp(speed / 45, 0, 1));
  }

  #pulseRaw(intensity: number, durationMs: number) {
    const actuators = (this.#clubSource?.gamepad as any)?.hapticActuators;
    const actuator = actuators?.[0];
    if (!actuator?.pulse) return;
    try {
      actuator.pulse(intensity, durationMs);
    } catch { /* not all runtimes implement it */ }
  }

  #checkButton(gamepad: Gamepad, sourceIndex: number, buttonIndex: number, onPress: () => void) {
    const key = `${sourceIndex}:${buttonIndex}`;
    const pressed = !!gamepad.buttons[buttonIndex]?.pressed;
    if (pressed && !(this.#buttonsDown.get(key) ?? false)) onPress();
    this.#buttonsDown.set(key, pressed);
  }

  /**
   * Rotation taking the canonical club (shaft along -Z, face along +X) onto a
   * world shaft/face pair — the same basis ClubOverlay builds its club with, so
   * the outline and the solved geometry can't disagree.
   */
  #basisFor(shaft: THREE.Vector3, face: THREE.Vector3): THREE.Quaternion {
    this.#axisZ.copy(shaft).normalize().negate();
    this.#axisX.copy(face).projectOnPlane(this.#axisZ).normalize();
    this.#axisY.crossVectors(this.#axisZ, this.#axisX).normalize();
    this.#matrix.makeBasis(this.#axisX, this.#axisY, this.#axisZ);
    return this.#basisQuaternion.setFromRotationMatrix(this.#matrix);
  }

  /**
   * Head (impact point) = down the shaft, then FACE_OFFSET toe-ward. The shaft
   * enters a real club at the HEEL; the face centre — what actually meets the
   * ball — sits `club.faceOffset` toe-ward of that line.
   *
   * Toe direction: the club frame is X=face, Z=−shaft, Y=Z×X; the heel sits at
   * +Y for a right-hander (mirrored geometry for lefties), so
   * toe = −(−shaft × face)·handSign — the same axis the strike-location code
   * already uses at impact.
   */
  #recomputeHead() {
    this.#heelAxis
      .crossVectors(this.#scratch.copy(this.#shaftLocal).negate(), this.#faceLocal)
      .normalize()
      .multiplyScalar(this.handSign);
    this.#headLocal.copy(this.#buttLocal)
      .addScaledVector(this.#shaftLocal, this.club.length)
      .addScaledVector(this.#heelAxis, -this.club.faceOffset); // −heel = toe
  }

  /**
   * The club head just moved relative to the controller, so the previous head
   * position belongs to a different club. Keeping it would present the jump as
   * one frame of travel — and since calibration can happen with the head *on the
   * ball*, that phantom sweep would fire a shot the instant the player calibrates.
   *
   * Disarming too (rather than only dropping the history) is what makes the flow
   * work: the head may be sitting at the ball, so the detector has to wait for
   * the club to be taken back before it will accept a strike.
   */
  #invalidatePose() {
    this.#hasPrevious = false;
    this.#hasPrevPrev = false;
    this.#armed = false;
    this.#lastImpactAt = this.#elapsed;
  }

  #applyPreset() {
    this.#shaftLocal.copy(this.#shaftAxis);
    this.#faceLocal.copy(this.#faceAxis).projectOnPlane(this.#shaftLocal).normalize();
    this.#buttLocal.copy(this.#shaftLocal).multiplyScalar(-this.#buttOffset);
    this.#leanBase = 0;
    this.#recomputeHead();
  }

  #loadGeometry() {
    this.#applyPreset();
    this.#invalidatePose();
    this.#calibrated = false;
    const raw = this.#store.read(this.#attachment);
    this.#localUpdatedAt = 0;
    if (!raw) return;
    try {
      const stored = JSON.parse(raw) as StoredGeometry;
      // Older entries stored a controller-anchored head instead of a butt
      // anchor — discard those rather than guess at a migration.
      if (!Array.isArray(stored.butt) || !Array.isArray(stored.shaft) || !Array.isArray(stored.face)) {
        throw new Error('stored geometry predates the butt-anchored model');
      }
      const butt = new THREE.Vector3().fromArray(stored.butt);
      const shaft = new THREE.Vector3().fromArray(stored.shaft);
      const face = new THREE.Vector3().fromArray(stored.face);
      if (butt.length() > MAX_BUTT_DIST || shaft.lengthSq() < 1e-4 || face.lengthSq() < 1e-4) {
        throw new Error('implausible stored geometry');
      }
      this.#buttLocal.copy(butt);
      this.#shaftLocal.copy(shaft.normalize());
      this.#faceLocal.copy(face.projectOnPlane(this.#shaftLocal).normalize());
      this.#leanBase = Number.isFinite(stored.lean) ? stored.lean : 0;
      this.#localUpdatedAt = Number.isFinite(stored.updatedAt) ? stored.updatedAt! : 0;
      this.#recomputeHead();
      this.#calibrated = true;
    } catch (error) {
      console.warn('XRSwingControls: ignoring stored club calibration', error);
      this.#applyPreset();
    }
  }

  #storeGeometry() {
    const stored: StoredGeometry = {
      butt: this.#buttLocal.toArray() as [number, number, number],
      shaft: this.#shaftLocal.toArray() as [number, number, number],
      face: this.#faceLocal.toArray() as [number, number, number],
      lean: this.#leanBase,
      updatedAt: Date.now(),
    };
    this.#localUpdatedAt = stored.updatedAt!;
    this.#store.write(this.#attachment, JSON.stringify(stored));
  }
}
